/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file serviceMode.js
 * @description
 * **Pure, React-free** resolution helpers for the "Service Modes" feature —
 * the dynamic version of the (until now hardcoded) car-service module
 * (work orders / vehicles / customers). A restaurant may apply a mode
 * (a backend `ServiceModeTemplate`, e.g. `preset:autoszerviz`) which turns
 * some of the 27 service features on/off, changes which fields are visible
 * on the work order / vehicle / customer forms, lists and prints, and swaps
 * in its own brand/task/photo/part catalogs.
 *
 * **FAIL-OPEN is the whole point of this file.** No config, a missing key,
 * or a fetch failure must all resolve to *exactly* today's behaviour:
 * every feature enabled, every field visible, the bundled catalogs. Only an
 * *explicit* `false`/empty override ever hides something. `tickets` and
 * `customers` (see {@link CORE_FEATURES}) can never be disabled, mirroring
 * the backend's own `sanitizeFeatureToggles` guarantee.
 *
 * This module has **no React import** — it is unit-testable in plain Node
 * (see `app/tests/serviceMode.test.js`) and is the single source of truth
 * consumed by `ServiceModeContext.js` (the stateful/React half).
 *
 * @see app/helpers/ServiceModeContext.js React provider/hook built on this.
 * @see app/config/serviceFieldSets.js Declarative field descriptors for forms.
 */

import carData from '../data/car_database.json';
import {TASK_CATEGORIES, REPAIR_TASKS, PART_CATEGORIES} from '../data/repairCatalog';

/**
 * The default photo categories for repair-order photo documentation. Moved
 * here (out of `RepairPhotoScreen.js`) so it is one of the swappable
 * catalogs; the screen re-imports it for back-compat.
 * @type {Array<{key: string, icon: string, color: string}>}
 */
export const PHOTO_CATEGORIES = [
    {key: 'motor_number', icon: 'engine', color: '#42A5F5'},
    {key: 'vin_plate', icon: 'card-text-outline', color: '#66BB6A'},
    {key: 'damage', icon: 'alert-circle', color: '#f5576c'},
    {key: 'parts', icon: 'cog', color: '#FFA726'},
    {key: 'before_after', icon: 'compare', color: '#AB47BC'},
    {key: 'other', icon: 'image-multiple', color: '#78909C'},
];

/**
 * Fuel-type options for the vehicle spec form. Moved out of
 * `VehicleSpecSelector.js` (re-exported there for back-compat).
 * @type {Array<{value: string, label: string}>}
 */
export const FUEL_OPTIONS = [
    {value: 'Petrol', label: 'Benzin'},
    {value: 'Diesel', label: 'Dizel'},
    {value: 'Hybrid_Petrol', label: 'Hibrid (Benzin)'},
    {value: 'Hybrid_Diesel', label: 'Hibrid (Dizel)'},
    {value: 'Electric', label: 'Elektromos'},
    {value: 'LPG', label: 'LPG / Benzin'},
    {value: 'CNG', label: 'CNG / Benzin'},
];

/**
 * Service-requirement / specialization options for the new work-order
 * wizard. Moved out of `NewWorkSheetModal.js` (re-exported there for
 * back-compat).
 * @type {Array<{value: string, labelKey: string, icon: string}>}
 */
export const SERVICE_REQUIREMENTS = [
    {value: 'bodywork', labelKey: 'repair_req_bodywork', icon: 'car-wrench'},
    {value: 'painter', labelKey: 'repair_req_painter', icon: 'format-paint'},
    {value: 'auto_electrician', labelKey: 'repair_req_auto_electrician', icon: 'lightning-bolt'},
    {value: 'chassis', labelKey: 'repair_req_chassis', icon: 'car-cog'},
    {value: 'mechanic', labelKey: 'repair_req_mechanic', icon: 'wrench'},
];

/**
 * Bundled vehicle brand catalog, normalized from `car_database.json`
 * (`{brand, models}`) to the backend `ServiceBrand` shape (`{name, models}`)
 * so it merges cleanly with `/service-brands/effective` rows.
 * @type {Array<{name: string, models: string[], source: 'bundled'}>}
 */
const BUNDLED_BRANDS = (Array.isArray(carData) ? carData : []).map((entry) => ({
    name: entry.brand,
    models: Array.isArray(entry.models) ? entry.models : [],
    source: 'bundled',
}));

/**
 * The full "Autoszerviz" catalog set, assembled from today's hardcoded data.
 * Applying `preset:autoszerviz` on the backend reproduces exactly this, so
 * "no config" and "autoszerviz applied" render identically.
 * @type {object}
 */
export const DEFAULT_CATALOGS = {
    taskCategories: TASK_CATEGORIES,
    defaultTasks: REPAIR_TASKS,
    partCategories: PART_CATEGORIES,
    photoCategories: PHOTO_CATEGORIES,
    fuelOptions: FUEL_OPTIONS,
    serviceRequirements: SERVICE_REQUIREMENTS,
    brands: BUNDLED_BRANDS,
    serviceTypes: [],
    workflowStages: [],
    serviceOptions: [],
};

/** Default hourly labor rate (Ft), used when no mode overrides it. */
export const DEFAULT_HOURLY_RATE = 8000;

/** Default VAT rate (%), used when no mode overrides it. */
export const DEFAULT_VAT_RATE = 27;

/**
 * Feature keys that can never be disabled — the service module's tickets
 * (work orders) and customers stay reachable no matter which mode is
 * applied, mirroring the backend's `CORE` feature-registry constant.
 * @type {string[]}
 */
export const CORE_FEATURES = ['tickets', 'customers'];

/** All 27 governable service-module feature keys (backend registry order). */
export const ALL_FEATURE_KEYS = [
    'dashboard', 'tickets', 'bookings', 'kanban', 'vehicles', 'devices', 'customers',
    'tyreHotel', 'partsWarehouse', 'quotes', 'calculator', 'statistics', 'documents',
    'importExport', 'statusWorkflow', 'priorities', 'tasksLabour', 'partsCatalog',
    'freeTextParts', 'timeTracking', 'checklist', 'tags', 'commentsTimeline', 'photos',
    'invoicing', 'contractGeneration', 'print',
];

/**
 * Alias map for field keys that differ between the mobile form model and the
 * backend field registry (e.g. the app calls it `plate`, the registry
 * `licensePlate`).
 * @type {Object.<string, string>}
 */
const FIELD_ALIASES = {
    plate: 'licensePlate',
    mileage: 'kilometers',
};

/**
 * Resolve whether a feature is enabled — **fail-open**: no config, a missing
 * key, or anything other than an explicit `false` all resolve to enabled.
 * Core features (see {@link CORE_FEATURES}) are always enabled.
 * @param {?object} config Effective service config (`GET /service/config` response), or null/undefined.
 * @param {string} key One of {@link ALL_FEATURE_KEYS}.
 * @returns {boolean} True unless the mode explicitly turned the key off.
 */
export const resolveFeatureEnabled = (config, key) => {
    if (!key || CORE_FEATURES.includes(key)) {
        return true;
    }
    const toggles = config?.effectiveFeatureToggles;
    if (!toggles || typeof toggles !== 'object') {
        return true;
    }
    return toggles[key] !== false;
};

/**
 * Resolve whether a field is visible on a given surface — **fail-open**: no
 * config or an entry missing for the key resolves to visible.
 * @param {?object} config Effective service config, or null/undefined.
 * @param {'workOrder'|'vehicle'|'customer'} entity Field-registry entity.
 * @param {string} key Field key (form vocabulary — aliases are resolved via {@link FIELD_ALIASES}).
 * @param {'form'|'list'|'print'} [surface] Which visibility flag to check.
 * @returns {boolean} True unless the mode explicitly hid the field on that surface.
 */
export const resolveFieldVisible = (config, entity, key, surface = 'form') => {
    const resolvedKey = FIELD_ALIASES[key] || key;
    const list = config?.resolvedFieldSettings?.[entity];
    if (!Array.isArray(list) || list.length === 0) {
        return true;
    }
    const entry = list.find((f) => f && f.key === resolvedKey);
    if (!entry) {
        return true;
    }
    if (entry.visible === false) {
        return false;
    }
    if (surface === 'list') {
        return entry.showInList !== false;
    }
    if (surface === 'print') {
        return entry.showInPrint !== false;
    }
    return true;
};

/**
 * Resolve a catalog list/number by name — a non-empty array (or, for
 * numeric catalogs like `hourlyRate`/`vatRate`, a finite number) from the
 * config wins; otherwise the bundled fallback is returned untouched.
 * @param {?object} config Effective service config, or null/undefined.
 * @param {string} name Catalog key, e.g. `taskCategories`, `brands`, `hourlyRate`.
 * @param {*} fallback Bundled default (array or number) to fall back to.
 * @returns {*} The resolved catalog.
 */
export const resolveCatalog = (config, name, fallback) => {
    const value = config?.[name] ?? config?.catalogs?.[name];
    if (Array.isArray(fallback)) {
        return Array.isArray(value) && value.length > 0 ? value : fallback;
    }
    if (typeof fallback === 'number') {
        return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    }
    return value !== undefined && value !== null ? value : fallback;
};

/**
 * Whether an error thrown by `apiFetch`/`apiRequest` (`WorkSheetContext.js`)
 * is a 403 "feature disabled" rejection — the string format is fixed there:
 * `` `API ${status}: ${body}` `` where `body` is the raw JSON/text response.
 * @param {*} err The caught error (any type — never throws).
 * @returns {boolean} True when this is a disabled-feature 403.
 */
export const isServiceFeatureDisabledError = (err) => {
    const message = err && typeof err === 'object' ? String(err.message || '') : String(err || '');
    return /^API 403:/.test(message) && message.includes('SERVICE_FEATURE_DISABLED');
};

/**
 * Union two brand lists by (case-insensitive) name — the remote/effective
 * list wins on conflicts, entries only in the bundled list are appended.
 * @param {Array<{name: string}>} remote Brands from `/service-brands/effective`.
 * @param {Array<{name: string}>} bundled Bundled fallback brands.
 * @returns {Array<object>} The merged, de-duplicated brand list.
 */
export const mergeBrands = (remote, bundled) => {
    const remoteList = Array.isArray(remote) ? remote : [];
    const bundledList = Array.isArray(bundled) ? bundled : [];
    const byName = new Map();

    bundledList.forEach((brand) => {
        const name = brand?.name || brand?.brand;
        if (name) {
            byName.set(String(name).toLowerCase(), brand);
        }
    });
    remoteList.forEach((brand) => {
        const name = brand?.name || brand?.brand;
        if (name) {
            byName.set(String(name).toLowerCase(), brand);
        }
    });

    return Array.from(byName.values());
};

/**
 * Clone a `managementDetailConfig.js`-shaped detail config, dropping only
 * fields the resolved field settings explicitly mark `visible: false` (and
 * any section left with zero fields afterwards). Fields absent from
 * `fieldSettings` (custom fields, computed metrics, …) always survive —
 * fail-open. A missing/empty `fieldSettings` returns `config` unchanged.
 * @param {object} config A `getManagementDetailConfig()` result (`{header, sections, ...}`).
 * @param {?Array<{key: string, visible?: boolean}>} fieldSettings The `workOrder` (or other entity) resolved field-settings list.
 * @returns {object} The filtered config (same shape, new object when anything was dropped).
 */
export const filterDetailConfigBySettings = (config, fieldSettings) => {
    if (!config || !Array.isArray(config.sections) || !Array.isArray(fieldSettings) || fieldSettings.length === 0) {
        return config;
    }

    const hiddenKeys = new Set(
        fieldSettings.filter((f) => f && f.visible === false).map((f) => f.key),
    );
    if (hiddenKeys.size === 0) {
        return config;
    }

    const sections = config.sections
        .map((section) => ({
            ...section,
            fields: (section.fields || []).filter((field) => !hiddenKeys.has(field.key)),
        }))
        .filter((section) => section.fields.length > 0);

    return {...config, sections};
};
